if (!customElements.get('landing-lead-form')) {
  class LandingLeadForm extends HTMLElement {
    static selectors = Object.freeze({
      form: '.landing-lead-form__form',
      modeSwitch: '[data-lead-mode-switch]',
      offerStateDescription: '[data-offer-state-description]',
      successMessage: '[data-success-message]',
      termsLabel: '.landing-lead-form__terms-label',
      errorOutput: '.landing-lead-form__output',
      submitButton: '.landing-lead-form__button--submit',
    });
    static controls = Object.freeze({
      languageIso: 'language_iso',
      languageRootUrl: 'language_root_url',
      channel: 'channel',
      phone: 'phone',
      email: 'email',
      terms: 'agree-to-terms',
      trackingConsent: 'agree-to-tracking',
    });
    static buttonVariants = Object.freeze({
      default: 'default',
      success: 'success',
      error: 'error',
    });
    static modes = Object.freeze({
      emailWhatsapp: 'email_whatsapp',
      emailOnly: 'email_only',
    });
    static offerStates = Object.freeze({
      emailWhatsapp: 'email_whatsapp',
      emailOnlyWithPhoneAvailable: 'email_only_with_phone_available',
      emailOnlyWhenPhoneHidden: 'email_only_when_phone_hidden',
    });
    static events = Object.freeze({
      leadSubmitSuccess: 'lead-submit-success',
      leadSubmitError: 'lead-submit-error',
    });
    static timeouts = Object.freeze({
      buttonMessageDuration: 3000,
      buttonMessageDurationExtended: 5000,
    });
    static consentLog = Object.freeze({
      endpoint: 'https://erebor.beglarianfabrics.com/webhook/bf/email-consent',
    });
    static errorTypes = Object.freeze({
      validation: 'validation',
      network: 'network',
      server: 'server',
      client: 'client',
    });

    static refStorageKey = 'bf_landing_ref';

    // ?ref= from the page URL, cleaned the same way as the n8n side (a-z 0-9 _ -, max 32).
    // Kept in sessionStorage so a language switch inside the same tab (query dropped)
    // does not lose it; storage may be blocked, so every access is guarded.
    static readRef() {
      const clean = (value) =>
        String(value || '')
          .toLowerCase()
          .replace(/[^a-z0-9_-]/g, '')
          .slice(0, 32);
      let fromUrl = '';
      try {
        fromUrl = clean(new URLSearchParams(window.location.search).get('ref'));
      } catch (_) {}
      try {
        if (fromUrl) {
          window.sessionStorage.setItem(LandingLeadForm.refStorageKey, fromUrl);
          return fromUrl;
        }
        return clean(window.sessionStorage.getItem(LandingLeadForm.refStorageKey));
      } catch (_) {
        return fromUrl;
      }
    }

    #isInitialized = false;
    #isSending = false;
    #isCopyMode = false;
    #buttonMessageTimeout = null;

    connectedCallback() {
      if (this.#isInitialized) return;

      // Single source of truth for this landing page — drives Klaviyo, the WA
      // webhook and the consent-log, so it never has to be kept in sync across
      // three separate hardcoded values (see docs/LEADS.md §3.1 for why that
      // used to be a problem for popup/bottom-sheet).
      this.source = this.dataset.source?.trim() || '';
      // Where inside the channel the visitor came from (?ref=dm / content / bio).
      // Sent to the WA webhook and the consent-log next to `source`; empty = not sent.
      this.ref = LandingLeadForm.readRef();
      this.klaviyo = {
        companyId: this.dataset.klaviyoCompanyId?.trim() || 'RH49u6',
        listId: this.dataset.klaviyoListId?.trim() || '',
        revision: this.dataset.klaviyoRevision?.trim() || '2026-01-15',
        consentVersion: this.dataset.consentVersion?.trim() || 'landing-v1-2026-08-18',
      };

      if (!this.source || !this.klaviyo.listId) {
        console.error('[landing-lead-form] missing required data-source or data-klaviyo-list-id — form disabled');
        return;
      }

      const form = this.querySelector(LandingLeadForm.selectors.form);
      const submitButton = form?.querySelector(LandingLeadForm.selectors.submitButton);

      this.elements = {
        form,
        languageIsoInput: form?.elements[LandingLeadForm.controls.languageIso],
        languageRootUrlInput: form?.elements[LandingLeadForm.controls.languageRootUrl],
        channelInput: form?.elements[LandingLeadForm.controls.channel],
        phoneInput: form?.elements[LandingLeadForm.controls.phone],
        emailInput: form?.elements[LandingLeadForm.controls.email],
        termsInput: form?.elements[LandingLeadForm.controls.terms],
        termsLabel: form?.querySelector(LandingLeadForm.selectors.termsLabel),
        trackingConsentInput: form?.elements[LandingLeadForm.controls.trackingConsent],
        submitButton,
        submitButtonText: submitButton?.querySelector('.btn__text') || submitButton?.firstElementChild || submitButton,
        output: form?.querySelector(LandingLeadForm.selectors.errorOutput),
        successMessageElement: this.querySelector(LandingLeadForm.selectors.successMessage),
        modeSwitchButtons: Array.from(this.querySelectorAll(LandingLeadForm.selectors.modeSwitch)),
        offerStateDescriptionElements: Array.from(this.querySelectorAll(LandingLeadForm.selectors.offerStateDescription)),
      };
      this.hidePhone = this.dataset.hidePhone === 'true';

      const requiredElements = [
        this.elements.form,
        this.elements.languageIsoInput,
        this.elements.languageRootUrlInput,
        this.elements.channelInput,
        this.elements.emailInput,
        this.elements.termsInput,
        this.elements.termsLabel,
        this.elements.submitButton,
        this.elements.submitButtonText,
        this.elements.output,
      ];
      if (!this.hidePhone) {
        requiredElements.push(this.elements.phoneInput);
      }

      if (!requiredElements.every((element) => element !== null)) return;

      this.supportedPhoneCodes = (this.dataset.supportedPhoneCodes || '')
        .split(',')
        .map((code) => code.trim())
        .filter(Boolean);

      this.offerConfig = {
        [LandingLeadForm.offerStates.emailWhatsapp]: {
          submitLabel: this.dataset.submitLabelDual || '',
          successMessage: this.dataset.successMessageDual || '',
        },
        [LandingLeadForm.offerStates.emailOnlyWithPhoneAvailable]: {
          submitLabel: this.dataset.submitLabelEmailOnlyWithPhoneAvailable || '',
          successMessage: this.dataset.successMessageEmailOnlyWithPhoneAvailable || '',
        },
        [LandingLeadForm.offerStates.emailOnlyWhenPhoneHidden]: {
          submitLabel: this.dataset.submitLabelEmailOnlyWhenPhoneHidden || '',
          successMessage: this.dataset.successMessageEmailOnlyWhenPhoneHidden || '',
        },
      };
      this.messages = {
        errorPhone: this.dataset.formErrorPhone,
        errorEmail: this.dataset.formErrorEmail,
        errorTerms: this.dataset.formErrorTerms,
        errorGeneric: this.dataset.formError,
        errorCountryNotSupported: this.dataset.countryNotSupported,
      };

      this.initialMode = this.resolveMode(this.dataset.modeDefault || this.elements.channelInput.value);

      this.onModeSwitchClick = this.onModeSwitchClick.bind(this);
      this.onSubmit = this.onSubmit.bind(this);

      this.elements.form.addEventListener('click', this.onModeSwitchClick);
      this.elements.form.addEventListener('submit', this.onSubmit);

      this.setMode(this.initialMode, { resetCopyMode: false });
      this.#isInitialized = true;
    }

    disconnectedCallback() {
      if (!this.#isInitialized || !this.elements?.form) return;

      this.elements.form.removeEventListener('click', this.onModeSwitchClick);
      this.elements.form.removeEventListener('submit', this.onSubmit);

      this.resetButtons();
      this.#isInitialized = false;
    }

    resolveMode(mode) {
      const normalized = String(mode || '').toLowerCase();
      if (normalized === 'whatsapp') return LandingLeadForm.modes.emailWhatsapp;
      if (normalized === 'email') return LandingLeadForm.modes.emailOnly;
      if (normalized !== LandingLeadForm.modes.emailWhatsapp && normalized !== LandingLeadForm.modes.emailOnly) {
        return this.hidePhone ? LandingLeadForm.modes.emailOnly : LandingLeadForm.modes.emailWhatsapp;
      }
      if (this.hidePhone && normalized === LandingLeadForm.modes.emailWhatsapp) {
        return LandingLeadForm.modes.emailOnly;
      }
      return normalized;
    }

    resolveOfferState(mode = this.currentMode) {
      if (mode === LandingLeadForm.modes.emailWhatsapp) {
        return LandingLeadForm.offerStates.emailWhatsapp;
      }
      return this.hidePhone
        ? LandingLeadForm.offerStates.emailOnlyWhenPhoneHidden
        : LandingLeadForm.offerStates.emailOnlyWithPhoneAvailable;
    }

    normalizeOfferState(value) {
      const normalized = String(value || '').toLowerCase();
      if (
        normalized === LandingLeadForm.offerStates.emailWhatsapp ||
        normalized === LandingLeadForm.offerStates.emailOnlyWithPhoneAvailable ||
        normalized === LandingLeadForm.offerStates.emailOnlyWhenPhoneHidden
      ) {
        return normalized;
      }
      if (normalized === LandingLeadForm.modes.emailWhatsapp || normalized === 'whatsapp') {
        return LandingLeadForm.offerStates.emailWhatsapp;
      }
      if (normalized === LandingLeadForm.modes.emailOnly || normalized === 'email') {
        return this.hidePhone
          ? LandingLeadForm.offerStates.emailOnlyWhenPhoneHidden
          : LandingLeadForm.offerStates.emailOnlyWithPhoneAvailable;
      }
      return this.resolveOfferState();
    }

    setMode(nextMode, { resetCopyMode = true } = {}) {
      const mode = this.resolveMode(nextMode);
      const isDualMode = mode === LandingLeadForm.modes.emailWhatsapp;
      const offerState = this.resolveOfferState(mode);

      this.currentMode = mode;
      this.currentOfferState = offerState;
      this.elements.form.dataset.channel = mode;
      this.elements.form.dataset.leadState = offerState;
      this.elements.form.dataset.hidePhone = String(this.hidePhone);
      this.elements.channelInput.value = mode;

      if (this.elements.phoneInput) {
        this.elements.phoneInput.disabled = this.hidePhone || !isDualMode;
        this.elements.phoneInput.required = !this.hidePhone && isDualMode;
        this.elements.phoneInput.setCustomValidity('');
      }
      this.elements.emailInput.setCustomValidity('');
      this.elements.termsInput.setCustomValidity('');

      this.errorMessage = '';
      this.updateOfferText(offerState);
      this.updateOfferDescriptions(offerState);
      this.updateModeSwitches(mode);

      if (resetCopyMode) {
        this.isCopyMode = false;
      }
    }

    updateOfferText(offerState) {
      const config = this.offerConfig[offerState] || {};
      const submitLabel = config.submitLabel || this.elements.submitButton.dataset.defaultText || '';

      this.elements.submitButton.dataset.defaultText = submitLabel;
      if (!this.isSending && !this.isCopyMode) {
        this.elements.submitButtonText.textContent = submitLabel;
      }

      if (this.elements.successMessageElement) {
        this.elements.successMessageElement.textContent = config.successMessage || '';
      }
    }

    updateOfferDescriptions(offerState) {
      this.elements.offerStateDescriptionElements.forEach((element) => {
        const elementOfferState = this.normalizeOfferState(element.dataset.offerStateDescription);
        element.hidden = elementOfferState !== offerState;
      });
    }

    updateModeSwitches(mode) {
      this.elements.modeSwitchButtons.forEach((button) => {
        if (this.hidePhone) {
          button.hidden = true;
          return;
        }
        button.hidden = false;
        const isSelected = this.resolveMode(button.dataset.modeTarget) === mode;
        button.dataset.selected = String(isSelected);
        button.setAttribute('aria-pressed', String(isSelected));
      });
    }

    onModeSwitchClick(event) {
      const switchButton = event.target.closest(LandingLeadForm.selectors.modeSwitch);
      if (!switchButton || this.isSending) return;

      const modeTarget = this.resolveMode(switchButton.dataset.modeTarget);
      if (modeTarget === this.currentMode) return;

      this.setMode(modeTarget, { resetCopyMode: true });
    }

    async onSubmit(event) {
      event.preventDefault();
      if (this.isSending) return;

      this.setButtonMessage(this.elements.submitButton.dataset.defaultText, LandingLeadForm.buttonVariants.default, 0);

      if (!this.validate()) {
        this.setButtonMessage('', LandingLeadForm.buttonVariants.error, LandingLeadForm.timeouts.buttonMessageDuration);

        this.emit(LandingLeadForm.events.leadSubmitError, {
          channel: this.currentOfferState,
          error_type: LandingLeadForm.errorTypes.validation,
        });
        return;
      }

      this.errorMessage = '';
      this.isSending = true;

      const mode = this.currentMode;
      const offerState = this.currentOfferState || this.resolveOfferState(mode);
      const normalizedPhone = this.elements.phoneInput ? this.normalizePhone(this.elements.phoneInput.value) : '';
      const email = this.elements.emailInput.value.trim();

      const basePayload = {
        language_iso: this.elements.languageIsoInput.value,
        language_root_url: this.elements.languageRootUrlInput.value,
        channel: offerState,
        phone: normalizedPhone,
        email,
        trackingConsent: this.elements.trackingConsentInput?.checked || false,
      };

      let submitError = null;
      try {
        if (mode === LandingLeadForm.modes.emailWhatsapp) {
          await Promise.all([
            this.sendEmailToKlaviyo({
              ...basePayload,
              phone: '',
            }),
            this.sendWhatsappToWebhook({
              ...basePayload,
              channel: 'whatsapp',
            }),
          ]);
        } else {
          await Promise.all([
            this.sendEmailToKlaviyo({
              ...basePayload,
              phone: '',
            }),
            this.sendConsentLog(basePayload),
          ]);
        }
      } catch (err) {
        submitError = err;
      }

      if (submitError) {
        let errorType = submitError?.errorType || LandingLeadForm.errorTypes.server;

        if ('cause' in submitError && submitError?.cause?.errors?.[0]?.code === 'invalid') {
          errorType = LandingLeadForm.errorTypes.validation;
          this.errorMessage = this.messages.errorPhone;
          this.setButtonMessage(
            '',
            LandingLeadForm.buttonVariants.error,
            LandingLeadForm.timeouts.buttonMessageDurationExtended,
          );
        } else if (errorType === LandingLeadForm.errorTypes.validation) {
          this.errorMessage = submitError.userMessage || submitError.message || this.messages.errorGeneric;
          this.setButtonMessage(
            '',
            LandingLeadForm.buttonVariants.error,
            LandingLeadForm.timeouts.buttonMessageDurationExtended,
          );
        } else {
          this.errorMessage = '';
          this.setButtonMessage(
            submitError.userMessage || this.messages.errorGeneric,
            LandingLeadForm.buttonVariants.error,
            LandingLeadForm.timeouts.buttonMessageDurationExtended,
          );
        }

        const emittedErrorType = mode === LandingLeadForm.modes.emailWhatsapp &&
          errorType === LandingLeadForm.errorTypes.network
          ? LandingLeadForm.errorTypes.server
          : errorType;

        this.emit(LandingLeadForm.events.leadSubmitError, {
          channel: offerState,
          error_type: emittedErrorType,
        });

        this.isSending = false;
        return;
      }

      try {
        this.resetForm();
        this.setMode(mode, { resetCopyMode: false });
        this.isCopyMode = true;
        this.emit(LandingLeadForm.events.leadSubmitSuccess, { channel: offerState });
      } catch (uiErr) {
        console.error('[landing-lead-form] success UI transition failed', {
          branch: 'ui',
          error: uiErr?.message || String(uiErr),
        });
        this.errorMessage = '';
        this.setButtonMessage(
          this.elements.submitButton.dataset.defaultText,
          LandingLeadForm.buttonVariants.default,
          0,
        );
        this.isSending = false;
      }
    }

    resetButtons({ resetSubmit = true } = {}) {
      clearTimeout(this.#buttonMessageTimeout);

      if (resetSubmit) {
        this.setButtonMessage(this.elements.submitButton.dataset.defaultText, LandingLeadForm.buttonVariants.default, 0);
      }
    }

    isSupportedPhoneCountry(phone) {
      if (this.supportedPhoneCodes.length === 0) return true;
      return this.supportedPhoneCodes.some((code) => phone.startsWith(code));
    }

    set isSending(value) {
      this.#isSending = Boolean(value);
      this.elements.submitButton.classList.toggle('is-loading', this.#isSending);
      if (this.#isSending) {
        this.elements.submitButton.setAttribute('aria-busy', 'true');
      } else {
        this.elements.submitButton.removeAttribute('aria-busy');
      }
    }

    get isSending() {
      return this.#isSending;
    }

    set isCopyMode(value) {
      this.#isCopyMode = Boolean(value);
      this.elements.form.dataset.copyMode = String(this.#isCopyMode);

      this.resetButtons({
        resetSubmit: !this.#isCopyMode,
      });
    }

    get isCopyMode() {
      return this.#isCopyMode;
    }

    set errorMessage(message) {
      this.elements.output.hidden = message === '';
      setTimeout(() => {
        this.elements.output.textContent = message;
      }, 0);
    }

    get errorMessage() {
      return this.elements.output.textContent || '';
    }

    setButtonMessage(
      message,
      variant = LandingLeadForm.buttonVariants.default,
      duration = LandingLeadForm.timeouts.buttonMessageDuration,
    ) {
      clearTimeout(this.#buttonMessageTimeout);

      if (message) {
        this.elements.submitButtonText.textContent = message;
      }
      this.elements.submitButton.dataset.variant = variant;

      if (duration <= 0) return;

      this.#buttonMessageTimeout = setTimeout(() => {
        if (message) {
          this.elements.submitButtonText.textContent = this.elements.submitButton.dataset.defaultText;
        }
        this.elements.submitButton.dataset.variant = LandingLeadForm.buttonVariants.default;
      }, duration);
    }

    clearFormValues() {
      if (this.elements.phoneInput) {
        this.elements.phoneInput.value = '';
        this.elements.phoneInput.setCustomValidity('');
      }
      this.elements.emailInput.value = '';
      this.elements.termsInput.checked = false;
      this.elements.emailInput.setCustomValidity('');
      this.elements.termsInput.setCustomValidity('');
    }

    resetForm() {
      this.clearFormValues();
      this.errorMessage = '';
      this.isCopyMode = false;
      this.isSending = false;
    }

    validate() {
      const mode = this.currentMode;
      const isDualMode = mode === LandingLeadForm.modes.emailWhatsapp &&
        Boolean(this.elements.phoneInput) &&
        !this.elements.phoneInput.disabled;

      if (this.elements.phoneInput) {
        this.elements.phoneInput.setCustomValidity('');
      }
      this.elements.emailInput.setCustomValidity('');
      this.elements.termsInput.setCustomValidity('');

      if (isDualMode) {
        const normalizedPhone = this.normalizePhone(this.elements.phoneInput.value);
        const phoneValidation = globalThis.LeadFormPhoneValidation?.validateInternationalPhone(normalizedPhone);
        const isPhoneValid = phoneValidation
          ? Boolean(phoneValidation.isValid)
          : /^\+[1-9]\d{1,14}$/.test(normalizedPhone);

        if (!isPhoneValid) {
          this.errorMessage = this.messages.errorPhone;
          this.elements.phoneInput.setCustomValidity(this.messages.errorPhone);
          this.elements.phoneInput.focus();
          return false;
        }

        if (!this.isSupportedPhoneCountry(normalizedPhone)) {
          const countryNotSupportedMessage = this.messages.errorCountryNotSupported ||
            this.messages.errorPhone ||
            this.messages.errorGeneric;
          this.errorMessage = countryNotSupportedMessage;
          this.elements.phoneInput.setCustomValidity(countryNotSupportedMessage);
          this.elements.phoneInput.focus();
          return false;
        }
      }

      if (!this.elements.emailInput.checkValidity()) {
        this.errorMessage = this.messages.errorEmail;
        this.elements.emailInput.setCustomValidity(this.messages.errorEmail);
        this.elements.emailInput.focus();
        return false;
      }

      if (!this.elements.termsInput.checkValidity()) {
        this.errorMessage = this.messages.errorTerms;
        this.elements.termsInput.setCustomValidity(this.messages.errorTerms);
        this.elements.termsInput.focus();
        return false;
      }

      this.errorMessage = '';
      return true;
    }

    normalizePhone(phone) {
      if (globalThis.LeadFormPhoneValidation?.normalizePhone) {
        return globalThis.LeadFormPhoneValidation.normalizePhone(phone);
      }
      return phone.trim().replace(/[^0-9+]/g, '');
    }

    async sendEmailToKlaviyo(payload) {
      const headers = {
        accept: 'application/vnd.api+json',
        'content-type': 'application/vnd.api+json',
        revision: this.klaviyo.revision,
      };

      let body = null;

      try {
        body = JSON.stringify({
          data: {
            type: 'subscription',
            attributes: {
              profile: {
                data: {
                  type: 'profile',
                  attributes: {
                    ...(payload.email ? { email: payload.email } : {}),
                    ...(payload.phone ? { phone_number: payload.phone } : {}),
                    subscriptions: {
                      ...(payload.email ? { email: { marketing: { consent: 'SUBSCRIBED' } } } : {}),
                    },
                    properties: {
                      source: this.source,
                      signup_source: this.source,
                      channel: payload.channel,
                      language_iso: payload.language_iso,
                      language_root_url: payload.language_root_url,
                      email_tracking_consent: payload.trackingConsent || false,
                      consent_version: this.klaviyo.consentVersion,
                    },
                  },
                },
              },
              custom_source: this.source,
            },
            relationships: {
              list: {
                data: {
                  type: 'list',
                  id: this.klaviyo.listId,
                },
              },
            },
          },
        });
      } catch (err) {
        throw new Error(this.messages.errorGeneric, { cause: err });
      }

      const subscribeUrl = new URL('https://a.klaviyo.com/client/subscriptions/');
      subscribeUrl.searchParams.set('company_id', this.klaviyo.companyId);

      let response = null;
      try {
        response = await fetch(subscribeUrl, {
          method: 'POST',
          headers,
          body,
        });
      } catch (err) {
        console.error('[landing-lead-form] submit failed', {
          branch: 'email',
          status: null,
          error: err?.message || String(err),
        });
        throw err;
      }

      if (response.status !== 202 && !response.ok) {
        const responsePreview = await response
          .clone()
          .text()
          .then((value) => value.trim().slice(0, 200))
          .catch(() => '');
        const responseError = await response.json();
        console.error('[landing-lead-form] submit failed', {
          branch: 'email',
          status: response.status,
          response: responsePreview || responseError?.title || '',
        });
        throw new Error(responseError.title, { cause: responseError });
      }
    }

    async sendWhatsappToWebhook(payload) {
      const webhookUrlRaw = this.dataset.whatsappWebhook?.trim() || '';
      let webhookUrl = null;

      try {
        webhookUrl = new URL(webhookUrlRaw);
      } catch (_) {
        console.error('[landing-lead-form] submit failed', {
          branch: 'whatsapp',
          status: null,
          response: 'Invalid webhook URL',
        });
        throw this.createSubmitError(LandingLeadForm.errorTypes.server, this.messages.errorGeneric);
      }

      let response = null;
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 8000);

      try {
        response = await fetch(webhookUrl.toString(), {
          method: 'POST',
          signal: controller.signal,
          headers: {
            'content-type': 'application/json',
            accept: 'application/json',
          },
          body: JSON.stringify({
            channel: payload.channel || 'whatsapp',
            phone: payload.phone,
            email: payload.email || '',
            consent: true,
            language_iso: payload.language_iso,
            language_root_url: payload.language_root_url,
            source: this.source,
            ...(this.ref ? { ref: this.ref } : {}),
          }),
        });
      } catch (err) {
        console.error('[landing-lead-form] submit failed', {
          branch: 'whatsapp',
          status: null,
          error: err?.message || String(err),
        });
        throw this.createSubmitError(
          LandingLeadForm.errorTypes.network,
          this.messages.errorGeneric,
          err,
          this.messages.errorGeneric,
        );
      } finally {
        clearTimeout(timeoutId);
      }

      if (response.ok) return;

      let responseBody = null;
      let rawResponse = '';
      try {
        rawResponse = await response.text();
      } catch (_) {
        rawResponse = '';
      }

      if (rawResponse.trim() !== '') {
        try {
          responseBody = JSON.parse(rawResponse);
        } catch (_) {
          responseBody = null;
        }
      }

      const responseMessage = typeof responseBody?.message === 'string'
        ? responseBody.message.trim()
        : '';

      if (response.status === 422) {
        console.error('[landing-lead-form] submit failed', {
          branch: 'whatsapp',
          status: response.status,
          response: responseMessage || rawResponse.trim().slice(0, 200),
        });
        throw this.createSubmitError(
          LandingLeadForm.errorTypes.validation,
          responseMessage || this.messages.errorPhone || this.messages.errorGeneric,
        );
      }

      if (response.status === 429) {
        console.error('[landing-lead-form] submit failed', {
          branch: 'whatsapp',
          status: response.status,
          response: responseMessage || rawResponse.trim().slice(0, 200),
        });
        throw this.createSubmitError(
          LandingLeadForm.errorTypes.server,
          responseMessage || this.messages.errorGeneric,
        );
      }

      console.error('[landing-lead-form] submit failed', {
        branch: 'whatsapp',
        status: response.status,
        response: responseMessage || rawResponse.trim().slice(0, 200),
      });
      throw this.createSubmitError(
        LandingLeadForm.errorTypes.server,
        responseMessage || this.messages.errorGeneric,
      );
    }

    async sendConsentLog(payload) {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 8000);

      try {
        await fetch(LandingLeadForm.consentLog.endpoint, {
          method: 'POST',
          signal: controller.signal,
          headers: {
            'content-type': 'application/json',
            accept: 'application/json',
          },
          body: JSON.stringify({
            email: payload.email,
            consent_version: this.klaviyo.consentVersion,
            email_tracking_consent: payload.trackingConsent || false,
            source: this.source,
            channel: payload.channel,
            ...(this.ref ? { ref: this.ref } : {}),
          }),
        });
      } catch (err) {
        console.error('[landing-lead-form] consent log failed', {
          branch: 'consent-log',
          error: err?.message || String(err),
        });
      } finally {
        clearTimeout(timeoutId);
      }
    }

    createSubmitError(errorType, message, cause = null, userMessage = null) {
      const error = cause ? new Error(message, { cause }) : new Error(message);
      error.errorType = errorType;
      error.userMessage = userMessage || message;
      return error;
    }

    emit(name, detail) {
      window.dispatchEvent(new CustomEvent(name, { detail }));
    }
  }

  customElements.define('landing-lead-form', LandingLeadForm);
}
